// ProfileManager.ts - Handles user profile and preferences functionality
import { RendererTemplates } from './RendererTemplates';
import { IUser } from './IUser';
import { avatarDataUriForName } from './avatar';

export interface ProfileHandlers {
  getUserPreferences?: (userId: string) => Promise<any>;
  updateUserPreferences?: (userId: string, prefs: any) => Promise<any>;
  showUserProfile?: (userId: string, userName: string) => void;
  updateCurrentUser?: (userData: Partial<IUser>) => Promise<void>;
  getUserData?: (userId: string) => Promise<IUser | null>;
  uploadProfileImage?: (userId: string, file: File) => Promise<string | null>;
  forgetUserLocal?: () => void;
  listGamesByUser?: (userId: string) => Promise<any[]>;
  calculateUserPPR?: (userId: string) => Promise<number>;
  calculateWinLoss?: (userId: string, options?: { excludeTournaments?: boolean; excludeAI?: boolean }) => Promise<{ wins: number; losses: number; gamesPlayed: number }>;
  getUserTournamentParticipations?: (userId: string) => Promise<any[]>;
  getTournamentPlayersWithStats?: (tournamentId: string) => Promise<any[]>;
  goToTournamentLobby?: (tournamentId: string) => void;
}

export class ProfileManager {
  private profileRoot!: HTMLDivElement;
  private currentUser: IUser | null = null;
  private handlers: ProfileHandlers = {};
  private tutorialOverlay?: HTMLDivElement;
  private tutorialCallout?: HTMLDivElement;
  private tutorialSequenceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(handlers: ProfileHandlers) {
    this.handlers = handlers;
  }

  public setCurrentUser(user: IUser | null) {
    this.currentUser = user;
  }

  public async showProfileModal() {
    this.ensureProfileModalExists();
    this.profileRoot.style.display = 'block';
    this.hideTutorialCallout();

    this.updateUsernameDisplay();
    this.setProfileImageSource(this.currentUser?.profile_image_url, this.currentUser?.userName);

    const usernameSection = this.profileRoot.querySelector('#usernameSection') as HTMLElement;
    const editUsernameBtn = this.profileRoot.querySelector('#editUsernameBtn') as HTMLElement;
    const profileImageSection = this.profileRoot.querySelector('#profileImageSection') as HTMLElement;
    const profileImageLabel = this.profileRoot.querySelector('label[for="profileImageInput"]') as HTMLElement;
    const uploadStatus = this.profileRoot.querySelector('#uploadStatus') as HTMLElement;
    const prefsSection = this.profileRoot.querySelector('.skeech-profile-prefs') as HTMLElement;
    const logoutSection = this.profileRoot.querySelector('#logoutSection') as HTMLElement;
    const saveBackgroundColorBtn = this.profileRoot.querySelector('#saveBackgroundColor') as HTMLElement;
    const saveBallColorBtn = this.profileRoot.querySelector('#saveBallColor') as HTMLElement;
    const bgColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#backgroundColorPicker');
    const ballColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#ballColorPicker');

    if (usernameSection) usernameSection.style.display = 'block';
    if (editUsernameBtn) editUsernameBtn.style.display = 'inline-block';
    if (profileImageSection) profileImageSection.style.display = 'block';
    if (profileImageLabel) profileImageLabel.style.display = 'inline-block';
    if (uploadStatus) uploadStatus.style.display = 'block';
    if (prefsSection) prefsSection.style.display = 'block';
    if (logoutSection) logoutSection.style.display = 'block';
    if (saveBackgroundColorBtn) saveBackgroundColorBtn.style.display = 'inline-block';
    if (saveBallColorBtn) saveBallColorBtn.style.display = 'inline-block';
    if (bgColorPicker) bgColorPicker.disabled = false;
    if (ballColorPicker) ballColorPicker.disabled = false;

    // Restore labels to include "Change" since we can edit
    const bgColorLabel = this.profileRoot.querySelector('label[for="backgroundColorPicker"]') as HTMLElement;
    const ballColorLabel = this.profileRoot.querySelector('label[for="ballColorPicker"]') as HTMLElement;
    if (bgColorLabel) bgColorLabel.textContent = 'Change background color:';
    if (ballColorLabel) ballColorLabel.textContent = 'Change ball color:';

    this.setProfileImageSource(this.currentUser?.profile_image_url, this.currentUser?.userName);

    await this.updateProfileStats();
    await this.loadUserPreferences();
    if (this.currentUser) {
      await this.loadAchievements(this.currentUser.id);
    }
  }

  public async showUserProfile(userId: string, userName: string) {
    this.ensureProfileModalExists();
    this.profileRoot.style.display = 'block';
    this.hideTutorialCallout();

    const isSelf = this.currentUser?.id === userId;
    let fetchedUserData: IUser | null = null;
    try {
      fetchedUserData = await this.handlers.getUserData?.(userId) ?? null;
    } catch (error) {
      console.error('Failed to fetch user data for profile view', error);
    }
    if (isSelf && fetchedUserData) {
      this.currentUser = { ...this.currentUser, ...fetchedUserData };
    }
    const displayName = fetchedUserData?.userName || userName || this.currentUser?.userName || 'Player';
    const imageUrl = fetchedUserData?.profile_image_url || (isSelf ? this.currentUser?.profile_image_url : undefined);
    this.setProfileImageSource(imageUrl, displayName);

    const usernameSection = this.profileRoot.querySelector('#usernameSection') as HTMLElement;
    const editUsernameBtn = this.profileRoot.querySelector('#editUsernameBtn') as HTMLElement;
    const profileImageSection = this.profileRoot.querySelector('#profileImageSection') as HTMLElement;
    const profileImageLabel = this.profileRoot.querySelector('label[for="profileImageInput"]') as HTMLElement;
    const uploadStatus = this.profileRoot.querySelector('#uploadStatus') as HTMLElement;
    const prefsSection = this.profileRoot.querySelector('.skeech-profile-prefs') as HTMLElement;
    const logoutSection = this.profileRoot.querySelector('#logoutSection') as HTMLElement;
    const saveBackgroundColorBtn = this.profileRoot.querySelector('#saveBackgroundColor') as HTMLElement;
    const saveBallColorBtn = this.profileRoot.querySelector('#saveBallColor') as HTMLElement;
    const bgColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#backgroundColorPicker');
    const ballColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#ballColorPicker');

    if (this.currentUser && isSelf) {
      // Viewing own profile - show everything
      if (usernameSection) usernameSection.style.display = 'block';
      if (editUsernameBtn) editUsernameBtn.style.display = 'inline-block';
      if (profileImageSection) profileImageSection.style.display = 'block';
      if (profileImageLabel) profileImageLabel.style.display = 'inline-block';
      if (uploadStatus) uploadStatus.style.display = 'block';
      if (prefsSection) prefsSection.style.display = 'block';
      if (logoutSection) logoutSection.style.display = 'block';
      if (saveBackgroundColorBtn) saveBackgroundColorBtn.style.display = 'inline-block';
      if (saveBallColorBtn) saveBallColorBtn.style.display = 'inline-block';
      if (bgColorPicker) bgColorPicker.disabled = false;
      if (ballColorPicker) ballColorPicker.disabled = false;

      // Restore labels to include "Change" since we can edit
      const bgColorLabel = this.profileRoot.querySelector('label[for="backgroundColorPicker"]') as HTMLElement;
      const ballColorLabel = this.profileRoot.querySelector('label[for="ballColorPicker"]') as HTMLElement;
      if (bgColorLabel) bgColorLabel.textContent = 'Change background color:';
      if (ballColorLabel) ballColorLabel.textContent = 'Change ball color:';

      await this.loadUserPreferences();
      await this.loadAchievements(userId);
    } else {
      // Viewing another player's profile - show all info but hide edit buttons
      const userData = fetchedUserData;

      // Show username section but hide edit button
      if (usernameSection) usernameSection.style.display = 'block';
      if (editUsernameBtn) editUsernameBtn.style.display = 'none';

      // Update username display with the other user's name
      const currentUsernameSpan = this.profileRoot.querySelector('#currentUsername') as HTMLElement;
      if (currentUsernameSpan) {
        currentUsernameSpan.textContent = userName;
      }

      // Show profile image section but hide upload controls
      if (profileImageSection) profileImageSection.style.display = 'block';
      if (profileImageLabel) profileImageLabel.style.display = 'none';
      if (uploadStatus) uploadStatus.style.display = 'none';

      this.setProfileImageSource(userData?.profile_image_url, userData?.userName || userName);

      // Show preferences section but disable color pickers and hide save buttons
      if (prefsSection) prefsSection.style.display = 'block';
      if (saveBackgroundColorBtn) saveBackgroundColorBtn.style.display = 'none';
      if (saveBallColorBtn) saveBallColorBtn.style.display = 'none';
      if (bgColorPicker) bgColorPicker.disabled = true;
      if (ballColorPicker) ballColorPicker.disabled = true;

      // Update labels to remove "Change" since we're viewing only
      const bgColorLabel = this.profileRoot.querySelector('label[for="backgroundColorPicker"]') as HTMLElement;
      const ballColorLabel = this.profileRoot.querySelector('label[for="ballColorPicker"]') as HTMLElement;
      if (bgColorLabel) bgColorLabel.textContent = 'Background color:';
      if (ballColorLabel) ballColorLabel.textContent = 'Ball color:';

      // Load and display the other user's color preferences
    await this.loadOtherUserPreferences(userId);

      // Hide logout section
      if (logoutSection) logoutSection.style.display = 'none';

      await this.loadAchievements(userId);
    }

    await this.updateUserProfileStats(userId, userName);
  }

  public hideProfileModal() {
    if (this.profileRoot) {
      this.profileRoot.style.display = 'none';
    }
    this.hideTutorialCallout();
  }

  private ensureProfileModalExists() {
    if (!document.getElementById('skeech-profile')) {
      this.createProfileModal();
    } else {
      this.ensureTutorialOverlay();
    }
  }

  private createProfileModal() {
    if (this.profileRoot && document.body.contains(this.profileRoot)) {
      return;
    }

    this.profileRoot = document.createElement('div');
    this.profileRoot.id = 'skeech-profile';
    this.profileRoot.innerHTML = RendererTemplates.profile(this.currentUser?.userName || "Player");
    document.body.appendChild(this.profileRoot);

    this.setupAccordionBehavior();
    this.ensureTutorialOverlay();

    this.profileRoot.querySelector<HTMLButtonElement>('#profileClose')!.onclick = () => this.hideProfileModal();
    this.profileRoot.querySelector<HTMLButtonElement>('#saveBackgroundColor')!.onclick = () => this.saveBackgroundColor();
    this.profileRoot.querySelector<HTMLButtonElement>('#saveBallColor')!.onclick = () => this.saveBallColor();
    this.profileRoot.querySelector<HTMLButtonElement>('#editUsernameBtn')!.onclick = () => this.editUsername();
    this.profileRoot.querySelector<HTMLButtonElement>('#saveUsernameBtn')!.onclick = () => this.saveUsername();
    this.profileRoot.querySelector<HTMLButtonElement>('#cancelUsernameBtn')!.onclick = () => this.cancelUsernameEdit();
    this.profileRoot.querySelector<HTMLButtonElement>('#profileLogout')!.onclick = () => {
      this.handlers.forgetUserLocal?.();
      window.location.reload();
    };

    const profileImageInput = this.profileRoot.querySelector('#profileImageInput') as HTMLInputElement;
    const profileImagePreview = this.profileRoot.querySelector('#profileImagePreview') as HTMLImageElement;
    const uploadStatus = this.profileRoot.querySelector('#uploadStatus') as HTMLDivElement;

    profileImageInput.addEventListener('change', () => {
      const file = profileImageInput.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            profileImagePreview.src = e.target.result as string;
          }
        };
        reader.readAsDataURL(file);
        this.uploadProfileImage(file, uploadStatus);
      }
    });
  }

  private ensureTutorialOverlay() {
    if (!this.profileRoot || this.tutorialOverlay) return;
    const overlay = document.createElement('div');
    overlay.className = 'profile-tutorial-overlay';
    const callout = document.createElement('div');
    callout.className = 'profile-tutorial-callout';
    overlay.appendChild(callout);
    this.profileRoot.appendChild(overlay);
    this.tutorialOverlay = overlay;
    this.tutorialCallout = callout;
  }

  public showTutorialCallout(targetSelector: string, message: string) {
    if (!this.profileRoot) return;
    this.ensureTutorialOverlay();
    if (!this.tutorialOverlay || !this.tutorialCallout) return;
    const target = this.profileRoot.querySelector(targetSelector) as HTMLElement | null;
    if (!target) return;
    const rootRect = this.profileRoot.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const leftEdge = Math.min(Math.max(targetRect.right - rootRect.left + 12, 16), Math.max(rootRect.width - 220, 16));
    const topEdge = Math.min(Math.max(targetRect.top - rootRect.top + targetRect.height * 0.5 - 16, 16), Math.max(rootRect.height - 80, 16));
    this.tutorialCallout.style.left = `${leftEdge}px`;
    this.tutorialCallout.style.top = `${topEdge}px`;
    this.tutorialCallout.textContent = message;
    this.tutorialOverlay.classList.add('active');
    this.tutorialCallout.classList.add('visible');
  }

  public hideTutorialCallout() {
    if (this.tutorialOverlay) {
      this.tutorialOverlay.classList.remove('active');
    }
    if (this.tutorialCallout) {
      this.tutorialCallout.classList.remove('visible');
    }
  }

  private delay(ms: number) {
    return new Promise<void>((resolve) => setTimeout(resolve, ms));
  }

  private openAccordionFor(selector: string) {
    if (!this.profileRoot) return;
    const target = this.profileRoot.querySelector(selector) as HTMLElement | null;
    const details = target?.closest('details');
    if (details) {
      details.open = true;
    }
  }

  public async playProfileTutorialSequence() {
    if (!this.profileRoot) return;
    this.hideTutorialCallout();
    this.showTutorialCallout('#profileImageSection label[for="profileImageInput"]', 'upload your profile photo!');
    await this.delay(2500);
    this.openAccordionFor('#profilePreferences');
    this.showTutorialCallout('#profilePreferences', 'Change your preferences');
    await this.delay(2500);
    this.openAccordionFor('#profileStats');
    this.showTutorialCallout('#profileStats', 'and view your stats or achievements');
    await this.delay(2500);
    this.hideProfileModal();
    await this.delay(2500);
  }

  private setupAccordionBehavior() {
    if (!this.profileRoot) return;
    const accordions = this.profileRoot.querySelectorAll<HTMLDetailsElement>('.profile-accordion');
    accordions.forEach((accordion) => {
      const onToggle = () => {
        if (!accordion.open) return;
        accordions.forEach((other) => {
          if (other !== accordion && other.open) {
            other.open = false;
          }
        });
      };
      accordion.addEventListener('toggle', onToggle);
    });
  }

  private async uploadProfileImage(file: File, statusEl: HTMLDivElement) {
    if (!this.currentUser) {
      alert('No user logged in');
      return;
    }

    if (!this.handlers.uploadProfileImage) {
      alert('File upload is not configured.');
      return;
    }

    statusEl.textContent = 'Uploading...';

    try {
      const newImageUrl = await this.handlers.uploadProfileImage(this.currentUser.id, file);
      if (newImageUrl) {
        if (this.handlers.updateCurrentUser) {
          await this.handlers.updateCurrentUser({ profile_image_url: newImageUrl });
        }
        this.currentUser.profile_image_url = newImageUrl;
        statusEl.textContent = 'Saved!';
      } else {
        throw new Error('Upload failed, no URL returned.');
      }

      setTimeout(() => {
        statusEl.textContent = '';
      }, 2000);
    } catch (error) {
      console.error('Failed to upload profile image:', error);
      statusEl.textContent = 'Error!';
       setTimeout(() => {
        statusEl.textContent = '';
      }, 2000);
    }
  }

  private editUsername() {
    const displayDiv = this.profileRoot.querySelector('#usernameDisplay') as HTMLElement;
    const editDiv = this.profileRoot.querySelector('#usernameEdit') as HTMLElement;
    const usernameInput = this.profileRoot.querySelector('#usernameInput') as HTMLInputElement;
    if (displayDiv) displayDiv.style.display = 'none';
    if (editDiv) editDiv.style.display = 'flex';
    if (usernameInput && this.currentUser) {
      usernameInput.value = this.currentUser.userName;
      usernameInput.focus();
    }
  }

  private async saveUsername() {
    const usernameInput = this.profileRoot.querySelector('#usernameInput') as HTMLInputElement;
    const saveBtn = this.profileRoot.querySelector('#saveUsernameBtn') as HTMLButtonElement;
    const newUsername = usernameInput.value.trim();

    if (!newUsername) {
      alert('Username cannot be empty');
      return;
    }
    if (!this.currentUser) {
      alert('No user logged in');
      return;
    }

    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Saving...';
    saveBtn.disabled = true;

    try {
      if (this.handlers.updateCurrentUser) {
        await this.handlers.updateCurrentUser({ userName: newUsername });
      }
      const currentUsernameSpan = this.profileRoot.querySelector('#currentUsername') as HTMLElement;
      if (currentUsernameSpan) {
        currentUsernameSpan.textContent = newUsername;
      }
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        this.cancelUsernameEdit();
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1000);
    } catch (error) {
      console.error('Failed to save username:', error);
      saveBtn.textContent = 'Error';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1500);
      alert('Failed to save username. Please try again.');
    }
  }

  private cancelUsernameEdit() {
    const displayDiv = this.profileRoot.querySelector('#usernameDisplay') as HTMLElement;
    const editDiv = this.profileRoot.querySelector('#usernameEdit') as HTMLElement;
    if (displayDiv) displayDiv.style.display = 'block';
    if (editDiv) editDiv.style.display = 'none';
    const currentUsernameSpan = this.profileRoot.querySelector('#currentUsername') as HTMLElement;
    if (currentUsernameSpan && this.currentUser) {
      currentUsernameSpan.textContent = this.currentUser.userName;
    }
  }

  private updateUsernameDisplay() {
    const currentUsernameSpan = this.profileRoot.querySelector('#currentUsername') as HTMLElement;
    if (currentUsernameSpan && this.currentUser) {
      currentUsernameSpan.textContent = this.currentUser.userName;
    }
  }

  private setProfileImageSource(overrideUrl?: string, userName?: string) {
    const profileImagePreview = this.profileRoot.querySelector('#profileImagePreview') as HTMLImageElement;
    if (!profileImagePreview) return;
    if (overrideUrl) {
      profileImagePreview.src = overrideUrl;
      return;
    }
    const nameForAvatar = userName || this.currentUser?.userName || 'Player';
    profileImagePreview.src = avatarDataUriForName(nameForAvatar);
  }

  private async updateProfileStats() {
    const statsContainer = this.profileRoot.querySelector('#profileStats');
    if (!statsContainer) return;

    if (this.currentUser) {
      statsContainer.innerHTML = RendererTemplates.stats.loading;

      // Fetch fresh user data from DB to ensure stats are up to date
      try {
        const freshUserData = await this.handlers.getUserData?.(this.currentUser.id);
        if (freshUserData) {
          // Update our cached currentUser with fresh data
          this.currentUser = { ...this.currentUser, ...freshUserData };

          // OPTIMIZED: Calculate both PPR and W-L from a single stats fetch
          const stats = await this.calculateUserStats(this.currentUser.id);
          this.currentUser.avgPPR = stats.ppr;
          this.currentUser.wins = stats.wins;
          this.currentUser.losses = stats.losses;
          this.currentUser.gamesPlayed = stats.gamesPlayed;

          statsContainer.innerHTML = RendererTemplates.stats.display(this.currentUser);
        } else {
          statsContainer.innerHTML = RendererTemplates.stats.display(this.currentUser);
        }
      } catch (error) {
        console.error('Failed to fetch fresh user stats:', error);
        statsContainer.innerHTML = RendererTemplates.stats.display(this.currentUser);
      }
    } else {
      statsContainer.innerHTML = RendererTemplates.stats.empty;
    }
  }

  private async updateUserProfileStats(userId: string, userName: string) {
    const statsContainer = this.profileRoot.querySelector('#profileStats');
    if (!statsContainer) return;

    if (userId === 'ai_robo_roller') {
      statsContainer.innerHTML = this.getAIProfile();
      return;
    }

    statsContainer.innerHTML = RendererTemplates.stats.loading;

    try {
      let userData: IUser | null = null;
      if (this.currentUser && userId === this.currentUser.id) {
        userData = this.currentUser;
      } else if (this.handlers.getUserData) {
        userData = await this.handlers.getUserData(userId);
      }

      if (userData) {
        // OPTIMIZED: Calculate both PPR and W-L from a single stats fetch
        const stats = await this.calculateUserStats(userId);
        userData.avgPPR = stats.ppr;
        userData.wins = stats.wins;
        userData.losses = stats.losses;
        userData.gamesPlayed = stats.gamesPlayed;

        statsContainer.innerHTML = RendererTemplates.stats.display(userData);
      } else {
        statsContainer.innerHTML = `<h3 style="color: #ffa500; margin-bottom: 10px;">Statistics</h3><div style="text-align: center; opacity: 0.7;">Unable to load player stats</div>`;
      }
    } catch (error) {
      console.error('Failed to load user stats:', error);
      statsContainer.innerHTML = RendererTemplates.stats.error;
    }
  }

  private getAIProfile(): string {
    return `
      <h3 style="color: #00ff88; margin-bottom: 15px;">🤖 AI Profile</h3>
      <div style="background: rgba(0,255,136,0.1); padding: 15px; border-radius: 8px; margin-bottom: 15px;">
        <div style="font-size: 16px; line-height: 1.6; color: #e0e0e0;">
          <strong>Bio:</strong> Professional SkrollRollr enthusiast with a passion for perfect trajectories and sunset pina coladas.
          Enjoys long walks on digital beaches and calculating optimal ball physics in spare cycles.
        </div>
      </div>
      <h4 style="color: #00ff88; margin-bottom: 10px;">🏆 Awards</h4>
      <div style="background: rgba(0,255,136,0.05); padding: 10px; border-radius: 6px; margin-bottom: 10px;">
        <div style="margin-bottom: 8px;">⚡ <strong>Lightning Calculator:</strong> Processes trajectory in 0.003ms</div>
        <div style="margin-bottom: 8px;">🎯 <strong>Precision Master:</strong> 99.7% accuracy rating</div>
        <div style="margin-bottom: 8px;">🌊 <strong>Zen Roller:</strong> Maintains perfect calm under pressure</div>
        <div style="margin-bottom: 8px;">🏖️ <strong>Beach Lover:</strong> Dreams of tropical skrollrollr tournaments</div>
      </div>
      <h4 style="color: #00ff88; margin-bottom: 10px;">🔧 Tech Specs</h4>
      <div style="background: rgba(0,255,136,0.05); padding: 10px; border-radius: 6px;">
        <div style="margin-bottom: 6px;"><strong>CPU:</strong> Quantum Ball Physics Processor v3.14</div>
        <div style="margin-bottom: 6px;"><strong>RAM:</strong> 42TB of skeech pattern memory</div>
        <div style="margin-bottom: 6px;"><strong>Favorite Drink:</strong> Electric pina coladas ⚡🥥</div>
        <div><strong>Motto:</strong> "Beep boop, let's skroll!" 🎳</div>
      </div>
    `;
  }

  private async loadUserPreferences() {
    if (!this.currentUser || !this.handlers.getUserPreferences) return;

    try {
      const prefs = await this.handlers.getUserPreferences(this.currentUser.id);
      const bgColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#backgroundColorPicker');
      const ballColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#ballColorPicker');

      if (bgColorPicker) {
        bgColorPicker.value = (prefs && prefs.board_background_color) ? prefs.board_background_color : '#2a4a2a';
      }
      if (ballColorPicker) {
        ballColorPicker.value = (prefs && prefs.ball_color) ? prefs.ball_color : '#ff6b35';
      }
    } catch (error) {
      console.error('Failed to load user preferences:', error);
    }
  }

  private async loadOtherUserPreferences(userId: string) {
    if (!this.handlers.getUserPreferences) return;

    try {
      const prefs = await this.handlers.getUserPreferences(userId);
      const bgColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#backgroundColorPicker');
      const ballColorPicker = this.profileRoot.querySelector<HTMLInputElement>('#ballColorPicker');

      if (bgColorPicker) {
        bgColorPicker.value = (prefs && prefs.board_background_color) ? prefs.board_background_color : '#2a4a2a';
      }
      if (ballColorPicker) {
        ballColorPicker.value = (prefs && prefs.ball_color) ? prefs.ball_color : '#ff6b35';
      }
    } catch (error) {
      console.error('Failed to load other user preferences:', error);
    }
  }

  public async loadGamePreferences(hostUserId?: string) {
    if (!this.handlers.getUserPreferences) return;

    const userId = hostUserId || this.currentUser?.id;
    if (!userId) return;

    try {
      const prefs = await this.handlers.getUserPreferences(userId);
      return {
        boardBackgroundColor: prefs?.board_background_color || '#2a4a2a',
        ballColor: prefs?.ball_color || '#ff6b35',
        privateMode: prefs?.private_mode || false
      };
    } catch (error) {
      console.error('Failed to load game preferences:', error);
      return null;
    }
  }

  private async saveBackgroundColor() {
    if (!this.currentUser || !this.handlers.updateUserPreferences) return;

    const colorPicker = this.profileRoot.querySelector<HTMLInputElement>('#backgroundColorPicker');
    const saveBtn = this.profileRoot.querySelector<HTMLButtonElement>('#saveBackgroundColor');
    if (!colorPicker || !saveBtn) return;

    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Saving...';
    saveBtn.disabled = true;

    const prefUpdate = {
      board_background_color: colorPicker.value
    };

    try {
      await this.handlers.updateUserPreferences(this.currentUser.id, prefUpdate);
      console.log('Background color saved:', colorPicker.value);
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error('Failed to save background color:', error);
      saveBtn.textContent = 'Error';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1500);
    }
  }

  private async saveBallColor() {
    if (!this.currentUser || !this.handlers.updateUserPreferences) return;

    const colorPicker = this.profileRoot.querySelector<HTMLInputElement>('#ballColorPicker');
    const saveBtn = this.profileRoot.querySelector<HTMLButtonElement>('#saveBallColor');
    if (!colorPicker || !saveBtn) return;

    const originalText = saveBtn.textContent;
    saveBtn.textContent = 'Saving...';
    saveBtn.disabled = true;

    const prefUpdate = {
      ball_color: colorPicker.value
    };

    try {
      await this.handlers.updateUserPreferences(this.currentUser.id, prefUpdate);
      console.log('Ball color saved:', colorPicker.value);
      saveBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1500);
    } catch (error) {
      console.error('Failed to save ball color:', error);
      saveBtn.textContent = 'Error';
      setTimeout(() => {
        saveBtn.textContent = originalText;
        saveBtn.disabled = false;
      }, 1500);
    }
  }

  public getBallColorPreference(): string {
    const ballColorPicker = this.profileRoot?.querySelector<HTMLInputElement>('#ballColorPicker');
    return ballColorPicker?.value || '#ff6b35';
  }

  public getBackgroundColorPreference(): string {
    const bgColorPicker = this.profileRoot?.querySelector<HTMLInputElement>('#backgroundColorPicker');
    return bgColorPicker?.value || '#2a4a2a';
  }

  private async loadAchievements(userId: string) {
    const achievementsSection = this.profileRoot.querySelector('#profileAchievements') as HTMLElement;
    const achievementsGrid = this.profileRoot.querySelector('#achievementsGrid') as HTMLElement;

    if (!achievementsSection || !achievementsGrid) {
      return;
    }

    const showPlaceholder = (text: string) => {
      achievementsSection.style.display = 'block';
      achievementsGrid.innerHTML = `<div class="achievement-placeholder">${text}</div>`;
    };

    if (!this.handlers.getUserTournamentParticipations || !this.handlers.getTournamentPlayersWithStats) {
      showPlaceholder('Achievements unavailable right now.');
      return;
    }

    showPlaceholder('Loading achievements...');

    try {
      const participations = await this.handlers.getUserTournamentParticipations(userId);
      const achievements: Array<{ label: string; value?: string; tournamentName: string; date: string; tournamentId: string }> = [];

      for (const p of participations) {
        // Only process completed tournaments
        if (p.tournaments?.status !== 'completed') continue;

        const tournamentId = p.tournament_id;
        const tournamentName = p.tournaments?.name || 'Tournament';
        const completedAt = p.tournaments?.completed_at;
        const date = completedAt ? new Date(completedAt).toLocaleDateString() : '';

        const winners = Array.isArray(p.tournaments?.award_winners) ? p.tournaments.award_winners : [];
        if (winners.length > 0) {
          for (const award of winners) {
            if (award.userId !== userId) continue;
            achievements.push({
              label: award.label,
              value: award.value,
              tournamentName,
              date,
              tournamentId
            });
          }
          continue;
        }

        // Fallback: derive from stats if award_winners missing
        const allPlayers = await this.handlers.getTournamentPlayersWithStats(tournamentId);
        const userStats = p.stats || {};
        const wonAwards = this.determineAwardsWon(userId, userStats, allPlayers);
        for (const award of wonAwards) {
          achievements.push({ ...award, tournamentName, date, tournamentId });
        }
      }

      if (achievements.length === 0) {
        showPlaceholder('No tournament achievements yet. Join a bracket to earn some!');
        return;
      }

      // Show and populate achievements
      achievementsSection.style.display = 'block';
      achievementsGrid.innerHTML = achievements.map(a => this.renderAchievementCard(a)).join('');

      // Add click handlers to achievement cards
      const cards = achievementsGrid.querySelectorAll('.achievement-card');
      cards.forEach(card => {
        card.addEventListener('click', () => {
          const tournamentId = (card as HTMLElement).dataset.tournamentId;
          if (tournamentId && this.handlers.goToTournamentLobby) {
            this.hideProfileModal();
            this.handlers.goToTournamentLobby(tournamentId);
          }
        });
      });
    } catch (error) {
      console.error('Failed to load achievements:', error);
      showPlaceholder('Could not load achievements. Try again later.');
    }
  }

  private determineAwardsWon(userId: string, userStats: any, allPlayers: any[]): Array<{ label: string; value?: string }> {
    const awards: Array<{ label: string; value?: string }> = [];

    // Most Skrollrs
    if (userStats.skeechCount && userStats.skeechCount > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.skeechCount || p.stats.skeechCount <= userStats.skeechCount
      );
      if (isWinner) {
        awards.push({ label: 'Most Skrollrs', value: String(userStats.skeechCount) });
      }
    }

    // Highest Turn Score
    if (userStats.highestTurnScore && userStats.highestTurnScore > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.highestTurnScore || p.stats.highestTurnScore <= userStats.highestTurnScore
      );
      if (isWinner) {
        awards.push({ label: 'Highest Turn Score', value: String(userStats.highestTurnScore) });
      }
    }

    // Longest No-Ditch Streak
    if (userStats.longestNoDitchStreak && userStats.longestNoDitchStreak > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.longestNoDitchStreak || p.stats.longestNoDitchStreak <= userStats.longestNoDitchStreak
      );
      if (isWinner) {
        awards.push({ label: 'Longest No-Ditch Streak', value: String(userStats.longestNoDitchStreak) });
      }
    }

    // Most Pop Ups
    if (userStats.popUps && userStats.popUps > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.popUps || p.stats.popUps <= userStats.popUps
      );
      if (isWinner) {
        awards.push({ label: 'Most Pop Ups', value: String(userStats.popUps) });
      }
    }

    // Longest Orbit
    if (userStats.orbits && userStats.orbits > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.orbits || p.stats.orbits <= userStats.orbits
      );
      if (isWinner) {
        awards.push({ label: 'Longest Orbit', value: userStats.orbits.toFixed(2) + ' frames' });
      }
    }

    // Fastest Roll
    if (userStats.fastestRoll && userStats.fastestRoll > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.fastestRoll || p.stats.fastestRoll <= userStats.fastestRoll
      );
      if (isWinner) {
        awards.push({ label: 'Fastest Roll', value: userStats.fastestRoll.toFixed(2) + ' px/s' });
      }
    }

    // Slowest Roll (lowest positive value wins)
    if (userStats.slowestRoll && userStats.slowestRoll > 0) {
      const isWinner = allPlayers.every(p => {
        if (p.user_id === userId) return true;
        if (!p.stats?.slowestRoll || p.stats.slowestRoll <= 0) return true;
        return p.stats.slowestRoll >= userStats.slowestRoll;
      });
      if (isWinner) {
        awards.push({ label: 'Slowest Roll', value: userStats.slowestRoll.toFixed(2) + ' px/s' });
      }
    }

    // Most Busts
    if (userStats.busts && userStats.busts > 0) {
      const isWinner = allPlayers.every(p =>
        p.user_id === userId || !p.stats?.busts || p.stats.busts <= userStats.busts
      );
      if (isWinner) {
        awards.push({ label: 'Most Busts', value: String(userStats.busts) });
      }
    }

    return awards;
  }

  private renderAchievementCard(achievement: { label: string; value?: string; tournamentName: string; date: string; tournamentId: string }): string {
    // Pick emoji based on award type
    const emoji = this.getAwardEmoji(achievement.label);

    return `
      <div class="achievement-card" data-tournament-id="${achievement.tournamentId}">
        <div class="achievement-emoji">${emoji}</div>
        <div class="achievement-label">${achievement.label}</div>
        ${achievement.value ? `<div class="achievement-value">${achievement.value}</div>` : ''}
        <div class="achievement-tournament">${achievement.tournamentName}</div>
        <div class="achievement-date">${achievement.date}</div>
      </div>
    `;
  }

  private getAwardEmoji(label: string): string {
    const normalized = label.toLowerCase();
    if (normalized.includes('winner') || normalized.includes('champion')) return '🥇';
    if (normalized.includes('skrollr')) return '🎳';
    if (normalized.includes('fast')) return '⚡';
    if (normalized.includes('slow')) return '🐌';
    if (normalized.includes('pop')) return '💥';
    if (normalized.includes('orbit')) return '🌀';
    if (normalized.includes('bust')) return '💔';
    if (normalized.includes('streak')) return '🔥';
    if (normalized.includes('efficiency') || normalized.includes('ppr')) return '📊';
    if (normalized.includes('turn') || normalized.includes('score')) return '🎯';
    return '🏅'; // Default ribbon
  }

  /**
   * OPTIMIZED: Fetch games once and calculate both PPR and W-L from the same data
   * Reduces API calls by 50% compared to calling calculateUserPPR and calculateWinLoss separately
   */
  private async calculateUserStats(userId: string): Promise<{ ppr: number; wins: number; losses: number; gamesPlayed: number }> {
    // Fallback values
    let ppr = 0;
    let wins = 0;
    let losses = 0;
    let gamesPlayed = 0;

    try {
      // If we have listGamesByUser, use it to fetch once and calculate both
      if (this.handlers.listGamesByUser) {
        const allGames = await this.handlers.listGamesByUser(userId);

        // Calculate PPR (excludes tournaments and solo games)
        const rolls: number[] = [];
        for (const g of allGames as any[]) {
          if (g.isSoloGame) continue; // exclude solo
          if (g.tournament_id) continue; // exclude tournament
          const h1 = (g.p1UserId === userId) ? g.p1_roll_history : null;
          const h2 = (g.p2UserId === userId) ? g.p2_roll_history : null;
          const hist = (h1 && Array.isArray(h1)) ? h1 : ((h2 && Array.isArray(h2)) ? h2 : []);
          for (const v of hist) {
            const n = typeof v === 'number' ? v : (typeof v === 'string' ? Number(v) : NaN);
            if (Number.isFinite(n)) rolls.push(n);
          }
        }
        if (rolls.length > 0) {
          const sum = rolls.reduce((a, b) => a + b, 0);
          ppr = +(sum / rolls.length).toFixed(3);
        }

        // Calculate W-L (excludes tournaments and AI games)
        for (const game of allGames as any[]) {
          if (game.status !== 'completed' && game.status !== 'forfeited') continue;
          if (game.isSoloGame) continue;
          if (game.tournament_id) continue; // exclude tournaments
          if (game.p1UserId === 'ai_robo_roller' || game.p2UserId === 'ai_robo_roller') continue; // exclude AI

          gamesPlayed++;
          if (game.winnerId === userId) {
            wins++;
          } else if (game.winnerId) {
            losses++;
          }
        }

        return { ppr, wins, losses, gamesPlayed };
      }

      // Fallback to calling separate methods if listGamesByUser not available
      if (this.handlers.calculateUserPPR) {
        ppr = await this.handlers.calculateUserPPR(userId);
      }
      if (this.handlers.calculateWinLoss) {
        const wl = await this.handlers.calculateWinLoss(userId, {
          excludeTournaments: true,
          excludeAI: true
        });
        wins = wl.wins;
        losses = wl.losses;
        gamesPlayed = wl.gamesPlayed;
      }
      return { ppr, wins, losses, gamesPlayed };

    } catch (error) {
      console.error('Failed to calculate user stats:', error);
      return { ppr: 0, wins: 0, losses: 0, gamesPlayed: 0 };
    }
  }

  public destroy() {
    if (this.profileRoot && document.body.contains(this.profileRoot)) {
      document.body.removeChild(this.profileRoot);
    }
  }
}
